import { Permissions } from '../../../../models/enums/permissions';
import * as React from 'react';
import { useDispatch, useStore } from 'react-redux';
import { PageBody } from '../../../../components/page-components/PageBody';
import { PageContent } from '../../../../components/page-components/PageContent';
import { PageHeading } from '../../../../components/page-components/PageHeading';
import { PagedList } from '../../../../components/paged-list/PagedList';
import { HasPermission } from '../../../../containers/authorization-helpers/HasPermission';
import { useScrollMemory } from '../../../../hooks/use-scroll-memory';
import { useSearchParams } from '../../../../hooks/use-search-params';
import { useShipmentSelection } from '../../../../hooks/use-shipment-selection';
import { PagedApiResult } from '../../../../models/utilities/paged-request';
import { StatusCode } from '../../../../utils/api';
import { PageNames, Routes, ScrollStatePaths } from '../../../../utils/app-constants';
import { navigateToPage } from '../../../../utils/history';
import * as transportActions from '../../../../transportation/actions';
import { ActionEditTruckInfo } from '../components/ActionEditTruckInfo';
import TruckRow from '../components/TruckRow';
import { ShipmentBulkActions } from '../../../../transportation/components/shipments/ShipmentBulkActions';
import { ShipmentType } from '../../../../transportation/models/enums/shipment-type';
import { TruckSearchFilters } from '../../../../transportation/models/filters/truck-search-filters';
import { TruckRead } from '../../../../transportation/models/trucks/truck-read';
import { TruckSummary } from '../../../../transportation/models/trucks/truck-summary';
import { getTruckSearchFilters } from '../../../../transportation/selectors';
import { TransportationSvc } from '../../../../transportation/service';
import '../../../../transportation/styles.scss';

export const TrucksScene = () => {

    const columns = ['truckId', 'status', 'name', 'driver', 'cutoff', 'departureDate'];

    const store     = useStore();
    const dispatch  = useDispatch();
    const [service] = React.useState(new TransportationSvc());
    const [editTruck, setEditTruck] = React.useState(new TruckRead());

    const [showInfoFlyout, setShowInfoFlyout] = React.useState(false);
    
    const [filterParams, setFilterParams]   = React.useState(() => {
        return getTruckSearchFilters(store.getState(), columns);
    });
    
    const [deliveries, setDeliveries]       = React.useState(() => {
        const initial = new PagedApiResult<TruckSummary>();
        initial.pagination = filterParams.paginationInfo;
        initial.pagination.reusePaging = true;
        return initial;
    });

    const [isBulkBusy, setIsBulkBusy] = React.useState(false);

    const { selected, selectedResetter, onSelectionFinish, resetSelection } = useShipmentSelection(
        deliveries.results,
        (row : any) => row.props.truck
    );

    const scrollTo = useScrollMemory(
        ScrollStatePaths.SHIPMENTS_DELIVERIES,
        Routes.SHIPPING_TRUCK_EDIT
    );

    useSearchParams(
        Routes.SHIPPING_TRUCK_EDIT,
        transportActions.updateTruckSearchFilters(filterParams),
        transportActions.clearTruckSearch()
    );
    
    /**
     * OnSearch handler
     * @param filters The list of filters to apply to the search
     */
    const onSearch = async (filters: TruckSearchFilters) => {
        resetSelection();
        const response = await service.search(TruckSummary, filters);
        if (response.status === StatusCode.Success) {
            setDeliveries(response.data);
            scrollTo();
        }

        setFilterParams(filters);
    };

    const onParamsUpdate = (itm : any) => {
        setFilterParams(itm);
    };

    const onParamsReset = () => {
        const tmp = new TruckSearchFilters(columns);
        setFilterParams(tmp);
    };

    const onBasicInfoFlyoutClose = async () => {
        setShowInfoFlyout(false);        
    };

    const onBasicInfoFlyoutSuccess = async (result : number) => {
        setEditTruck(new TruckRead());
        setShowInfoFlyout(false);

        // For now we will navigate to the newly created pickup.
        // Could be revisited later with a call to search
        // await onSearch(filterParams);
        // onSearch(filterParams);
        navigateToPage(Routes.SHIPPING_TRUCK_EDIT_WITH_ID.formattify(result));
    };

    return (<PageContent classes="deliveries-list" pageName={PageNames.TRANSPORTATION_TRUCK_LIST}>
                <PageHeading>
                    <HasPermission requiredPermissions={[
                        Permissions.ShippingTrucksEdit
                    ]}>
                        <button className="btn new attention" onClick={ () => setShowInfoFlyout(true) }>
                            Create Truck
                        </button>
                    </HasPermission>
                </PageHeading>
                <PageBody>
                    <ShipmentBulkActions
                        type={ ShipmentType.Truck }
                        shipments={ selected }
                        onRefresh={ () => onSearch(filterParams) }
                        onBusyChange={ setIsBulkBusy }>
                        <PagedList
                            noResultsMessage="No trucks found, please search again."
                            searchCallback={ onSearch }
                            data={ deliveries }
                            filterProps={ filterParams }
                            selectableClass=""
                            onFilterUpdate={ onParamsUpdate }
                            onReset={ onParamsReset }
                            reduxDispatch={ dispatch }
                            selectedResetter={ selectedResetter }
                            enabledDrag={ !isBulkBusy }
                            onDragComplete={ onSelectionFinish }
                            rowRenderer={(truck: TruckSummary, extraProps : any) => 
                                <TruckRow
                                    key={ truck.id }
                                    truck={ truck }
                                    { ...extraProps }
                                />
                            }
                        />
                    </ShipmentBulkActions>
                    
                    <ActionEditTruckInfo
                        visible={ showInfoFlyout }
                        onSuccess={ onBasicInfoFlyoutSuccess }
                        onClose={ onBasicInfoFlyoutClose }
                        truck={ editTruck }                    
                    />

                </PageBody>
            </PageContent>);
    
};
